import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as babaPacketsApi from '../api/babaPackets';

export function BabaPackets() {
  const { selectedOrg } = useOrg();
  const { canReadAssets, canWriteAssets } = usePermissions();
  const [packets, setPackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedPacket, setSelectedPacket] = useState(null);
  const [showIndexModal, setShowIndexModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    programId: 'program-nevi',
    siteId: '',
    name: ''
  });

  const loadPackets = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await babaPacketsApi.getBabaPackets(selectedOrg.id);
      setPackets(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets]);

  useEffect(() => {
    loadPackets();
  }, [loadPackets]);

  const handleOpenGenerate = () => {
    setGenerateForm({
      programId: 'program-nevi',
      siteId: '',
      name: ''
    });
    setShowGenerateModal(true);
  };

  const handleGenerate = async () => {
    if (!selectedOrg || generating) return;
    setGenerating(true);
    try {
      await babaPacketsApi.createBabaPacket(selectedOrg.id, {
        programId: generateForm.programId,
        siteId: generateForm.siteId || null,
        name: generateForm.name || undefined
      });
      setShowGenerateModal(false);
      loadPackets();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleViewIndex = async (packet) => {
    setSelectedPacket(packet);
    setShowIndexModal(true);
  };

  const handleDownload = async (packet) => {
    if (!selectedOrg) return;
    try {
      await babaPacketsApi.downloadPacket(selectedOrg.id, packet.id);
    } catch (err) {
      console.error('Failed to log download:', err);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view BABA packets.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="BABA Packets"
        subtitle="Generate and manage compliance packet archives"
      />

      <div style={styles.toolbar}>
        {canWriteAssets && (
          <button
            onClick={handleOpenGenerate}
            style={styles.generateButton}
            data-testid="generate-packet-button"
          >
            + Generate Packet
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading packets...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="packets-table">
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Program</th>
                <th style={styles.th}>Site</th>
                <th style={styles.th}>Documents</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {packets.map(packet => (
                <tr key={packet.id} style={styles.tr} data-testid={`packet-${packet.id}`}>
                  <td style={styles.td}>
                    <div style={styles.packetName}>{packet.name}</div>
                    <div style={styles.packetId}>v{packet.version}</div>
                  </td>
                  <td style={styles.td}>{packet.programId}</td>
                  <td style={styles.td}>{packet.siteId || 'All Sites'}</td>
                  <td style={styles.td}>{packet.documentCount}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      background: packet.status === 'completed' ? '#c6f6d5' : '#fefcbf',
                      color: packet.status === 'completed' ? '#276749' : '#975a16'
                    }}>
                      {packet.status}
                    </span>
                  </td>
                  <td style={styles.td}>{new Date(packet.createdAt).toLocaleString()}</td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleViewIndex(packet)}
                      style={styles.actionButton}
                      data-testid={`view-index-${packet.id}`}
                    >
                      View Index
                    </button>
                    <button
                      onClick={() => handleDownload(packet)}
                      style={styles.downloadButton}
                      data-testid={`download-${packet.id}`}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
              {packets.length === 0 && (
                <tr>
                  <td colSpan="7" style={styles.emptyState}>
                    No packets generated yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showGenerateModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="generate-modal">
            <h3 style={styles.modalTitle}>Generate BABA Packet</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Packet Name (optional)</label>
              <input
                type="text"
                value={generateForm.name}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, name: e.target.value }))}
                style={styles.input}
                placeholder="Auto-generated if empty"
                data-testid="packet-name-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Program</label>
              <select
                value={generateForm.programId}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, programId: e.target.value }))}
                style={styles.select}
                data-testid="packet-program-input"
              >
                <option value="program-nevi">NEVI</option>
                <option value="program-fleet">Fleet</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Site Scope (optional)</label>
              <input
                type="text"
                value={generateForm.siteId}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, siteId: e.target.value }))}
                style={styles.input}
                placeholder="Leave empty for all sites"
                data-testid="packet-site-input"
              />
            </div>
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowGenerateModal(false)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                style={styles.submitButton}
                data-testid="confirm-generate-button"
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showIndexModal && selectedPacket && (
        <div style={styles.modalOverlay}>
          <div style={styles.indexModal} data-testid="index-modal">
            <h3 style={styles.modalTitle}>Packet Index: {selectedPacket.name}</h3>
            <pre style={styles.indexContent}>
              {selectedPacket.indexContent || 'Index content not available'}
            </pre>
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowIndexModal(false)}
                style={styles.cancelButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '1rem'
  },
  generateButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568',
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem'
  },
  packetName: {
    fontWeight: '500'
  },
  packetId: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  statusBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  actionButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #3182ce',
    background: 'white',
    color: '#3182ce',
    fontSize: '0.75rem',
    cursor: 'pointer',
    marginRight: '0.5rem'
  },
  downloadButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #48bb78',
    background: '#48bb78',
    color: 'white',
    fontSize: '0.75rem',
    cursor: 'pointer'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '450px',
    maxWidth: '550px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  indexModal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '600px',
    maxWidth: '800px',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  modalTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  formGroup: {
    marginBottom: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#4a5568'
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  select: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  indexContent: {
    background: '#f7fafc',
    padding: '1rem',
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
    maxHeight: '400px'
  },
  modalActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem'
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  }
};
